import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Amplify, API, graphqlOperation } from 'aws-amplify';
import { getCurrentUser } from '@aws-amplify/auth';
import awsconfig from "./aws-exports";
import { TenantProvider } from "./context/TenantContext";
import LoginPage from "./component/LoginPage";
import RegisterPage from "./component/SignupPage";
import ConfirmEmailPage from "./component/ConfirmationPage";
import ForgotPasswordPage from "./component/ForgotPasswordPage";
import ResetPasswordPage from "./component/ResetPasswordPage";
import PricingPlans from "./component/PricingPlan";
import ProtectedRoute from "./component/ProtectedRoute";
import "toastr/build/toastr.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import ConnectedAccount from "./component/ConnectedAccount";
import SecureGuard from "./component/SecureGuard";
import DefenderScan from "./component/DefenderScan";
import UserDashboard from "./pages/UserdashboardPage";
import MSPApp from "./msp/MSPApp";
import AdminApp from "./admin/AdminApp";
import CompanyApp from "./company/CompanyApp";

Amplify.configure(awsconfig);

const App = () => {
  const [appType, setAppType] = useState('loading');
  const [mspSlug, setMspSlug] = useState(null);
  
  useEffect(() => {
    const checkAppType = () => {
      const hostname = window.location.hostname;
      const subdomain = hostname.split('.')[0];
      
      // For localhost testing, check query param: ?portal=admin or ?portal=noventiq
      const urlParams = new URLSearchParams(window.location.search);
      const portalParam = urlParams.get('portal');
      
      // Check if user is company type (from localStorage)
      const userType = localStorage.getItem('userType');
      const companyID = localStorage.getItem('companyID');
      
      // 🏢 COMPANY USER DETECTION
      // If user is logged in as company, always show company app
      if (userType === 'company' && companyID) {
        console.log('App.js: Detected company user, switching to CompanyApp');
        setAppType('company');
        return;
      }
      
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        if (portalParam === 'admin') {
          setAppType('admin');
        } else if (portalParam) {
          setAppType('msp');
          setMspSlug(portalParam);
        } else {
          setAppType('tenant');
        }
      } else if (subdomain !== 'app' && subdomain !== 'dev' && subdomain !== 'admin' && subdomain !== 'www') {
        setAppType('msp');
        setMspSlug(subdomain);
      } else if (subdomain === 'admin') {
        setAppType('admin');
      } else {
        setAppType('tenant');
      }
    };
    
    checkAppType();
    
    // Listen for storage changes (when login sets userType/companyID)
    const handleStorageChange = () => {
      checkAppType();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check on focus (when user comes back to tab after login)
    window.addEventListener('focus', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleStorageChange);
    };
  }, []);
  
  if (appType === 'loading') {
    return <div>Loading...</div>;
  }
  
  if (appType === 'msp') {
    return <MSPApp mspSlug={mspSlug} />;
  }
  
  if (appType === 'admin') {
    return <AdminApp />;
  }
  
  // 🏢 COMPANY APP
  if (appType === 'company') {
    return <CompanyApp />;
  }
  
  // Default tenant app
  return (
    <Router>
      <TenantProvider>
        <Routes>
          {/* Authentication Pages */}
          <Route element={<ProtectedRoute isAuthPage={true} />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/confirm-email" element={<ConfirmEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          {/* Protected Pages */}
          <Route element={<ProtectedRoute isAuthPage={false} />}>
            {/* COMMENTED OUT: Pricing page route - commented out for now, can be uncommented in future if needed */}
            {/* <Route path="/pricing" element={<PricingPlans />} /> */}
            <Route path="/connected-account" element={<ConnectedAccount />} />
            <Route path="/dashboard/*" element={<UserDashboard />} />
          </Route>

          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </TenantProvider>
    </Router>
  );
};

export default App;
